import { Animation, lerp } from "@kosmod/kit"
import { s, Shape } from "@kosmod/pancake"
import { Component, createContext, h, Node, ReadOnlyRef, useContext, useRef, useState } from "@lukekaalim/act"
import { v2 } from "@lukekaalim/act-router"
import { html } from "@lukekaalim/act-web"

export type PageDef = {
  path: string,

  title?: string,
  query?: Shape,
}
export const Page = {
  def <const T extends PageDef>(def: T): T {
    return def;
  },
  defRenderer<T extends PageDef>(def: T, renderer: Page.Renderer<T>): Page.Renderer<T> {
    return renderer;
  }
}
export const Site = {
  def <const T extends Site.Def>(def: T): T {
    return def;
  },
  defRenderer<const T extends Site, const R extends { [key in keyof Site.Renderer<T>]: Page.RendererDef<T[key]> }>(site: T, renderer: R ): Site.Renderer<T> {
    return Object.fromEntries(
      Object.entries(renderer).map(([key, renderer]) => {
        const def = site[key];

        return [key, { ...renderer, def }];
      })
    ) as Site.Renderer<T>
  }
}
export namespace Page {
  export type Def = {
    path: string,

    title?: string,
    query?: Shape,
  };
  export type State<T extends Def> = {
    def: T,
    path:  T["path"],
    query: T["query"] extends Shape ? Shape.Type.Of<T["query"]> : never,

    preloaded: boolean,
    navigationAnimation: Animation<number>,
  }
  export type PartialState<T extends Def> = {
    path:  T["path"],
    query?: null | (T["query"] extends Shape ? Shape.Type.Of<T["query"]> : never),

    preloaded?: boolean,
    navigationAnimation?: Animation<number>,
  }

  export type RendererProps<T extends Def> = { page: State<T>, onReady(): void }
  export type RendererDef<T extends Def> = {
    key?: string,
    needsLoading?: boolean,
    component: Component<RendererProps<T>>
  }
  export type Renderer<T extends Def> = {
    def: T,

    key?: string,
    needsLoading?: boolean,
    component: Component<RendererProps<T>>
  }
}
export type Page = Page.Def;

/**
 * A "Site" is a collection of Pages
 */
export namespace Site {
  export type Def = Record<string, Page.Def>;

  export type PageByPath<T extends Def, Path extends T["path"]> = Extract<T[keyof T], { path: Path }>;

  export type Page<TSite extends Def> = TSite[keyof TSite];

  export type PageState<TSite extends Def> = {
    [Index in keyof TSite]: Page.State<TSite[Index]>
  }[keyof TSite];

  export type PartialPageState<TSite extends Def> = {
    [Index in keyof TSite]: Page.PartialState<TSite[Index]>
  }[keyof TSite];

  export type Renderer<T extends Def> = {
    [Index in keyof T]?: Page.Renderer<T[Index]>
  }
}
export type Site = Site.Def;

export type NavigationResult = {
  mounted: Promise<void>,
  ready: Promise<void>,
  ref: ReadOnlyRef<HTMLElement | null>,
}

export type SiteManager<T extends Site.Def> = {
  site: T,

  navigate(
    target: URL | Site.PartialPageState<T>,
  ): void,

  router: v2.URLRouter,

  pages: Site.PageState<T>[],
}

const buildQueryObject = <T extends Shape | void>(url: URL, shape: T): T extends Shape ? Shape.Type.Of<T> : {} => {
  if (!shape)
    return {} as any;

  const params: Record<string, string> = {};
  for (const [key, value] of url.searchParams)
    params[key] = value;

  return params as any;
}
const buildSearchParams = (queryObject: Record<string, string>) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(queryObject))
    params.append(key, value)

  return params;
}

export const getPageStateFromURL = <T extends Site>(site: T, url: URL): null | Site.PageState<T> => {
  throw new Error();
}

export const useRootSiteManager = <T extends Site>(
  site: T,
  router: v2.URLRouter,
): SiteManager<T> => {
  const pageDefs = Object.values(site);

  const location = v2.useRouterLocation(router);

  const currentPage = pageDefs.find(def => def.path === location.pathname)!;

  const [preloaded, setPreloaded] = useState<Site.PageState<T>[]>([]);
  
  const query = buildQueryObject(location, currentPage.query) as any;
  
  const pages: Site.PageState<T>[] = [
    {
      path: location.pathname,
      query,
      def: currentPage!,
      preloaded: false,
      navigationAnimation: Animation(0, 0, [0, 0], ([a, b], p) => lerp(a, b, p))
    } as Site.PageState<T>,
    ...preloaded,
  ];

  return {
    navigate(target) {
      if (target instanceof URL) {
        router.go(target);
        // const targetPage = pageDefs.find(def => def.path === target.pathname)!;
        // const targetQuery = buildQueryObject(location, currentPage.query) as any;

        // const targetPageState = {
        //   path: targetPage.path,
        //   query: targetQuery,
        //   def: targetPage,
        //   preloaded: false,
        //   navigationAnimation: Animation(0, 0, [0, 0], ([a, b], p) => lerp(a, b, p))
        // } as Site.PageState<T>
      } else {
        const url = new URL(target.path, router.location);
        if (target.query)
          url.search = buildSearchParams(target.query).toString();

        router.go(url)
      }
      
    },
    site,
    router,
    pages: pages as any,
  }
};

export type PageRendererContainerProps = {
  preloaded: boolean,
};

export const PageRendererContainer: Component<PageRendererContainerProps> = ({ children, preloaded }) => {
  if (preloaded) {
    return h(html.section, { style: {
      position: 'absolute', pointerEvents: 'none', width: '100%', height: '100%',
      opacity: '0.2'
    } }, children);
  } else {
    return h(html.section, { style: { position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', zIndex: '1' } }, children);
  }
}

export type SiteRendererProps = {
  renderers: Site.Renderer<Site.Def>
};

export const usePageRenderer = <T extends Site.Def>(
  siteRenderer: Site.Renderer<T>,
  manager: SiteManager<T>,
) => {
  const renderers = Object.values(siteRenderer) as Site.Renderer<T>[keyof T][];

  const pageNodes = manager.pages.map(state => {
    const renderer = renderers.find(renderer => renderer?.def.path === state.path);
    if (!renderer)
      return 'Page exists but is has not associated renderer';

    return h(renderer.component, { page: state, onReady() {} })
  })

  return pageNodes;
}
